import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import {
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  ClipboardCheck,
  MapPin,
  Play,
  Plus,
  User,
  X,
} from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Label, PageContainer, PageHeader, Skeleton } from "@fleet/ui";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { ClipboardChecklistIllustration } from "@/components/ui/illustrations";
import { Select } from "@/components/ui/select";
import { Tabs, TabsList, TabsPanel, TabsPanels, TabsTrigger } from "@/components/ui/tabs";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { createPowerSyncQuery } from "@fieldcheck/shared/solid";
import {
  cn,
  createInspectionMutation,
  getInspectionsWithMetaQuery,
  getOrgMembersLocalQuery,
  getSitesQuery,
  getTemplatesQuery,
  type InspectionStatus,
  type InspectionWithMeta,
  queryKeys,
  type SiteRecord,
  type TemplateRecord,
} from "@fieldcheck/shared";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { useActiveContext } from "@/features/organizations/hooks/use-active-context";

export const STATUS_LABELS: Record<InspectionStatus, MessageDescriptor> = {
  draft: msg`Draft`,
  in_progress: msg`In progress`,
  completed: msg`Completed`,
};

type StatusFilter = "all" | InspectionStatus;

const isStatusFilter = (value: string): value is StatusFilter =>
  value === "all" || value === "draft" || value === "in_progress" || value === "completed";

type OrgMemberRow = {
  member_id: string;
  user_id: string | null;
  role: string | null;
  name: string | null;
  email: string | null;
};

export function InspectionsView() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_app/inspections" });
  const { userId } = session;
  const activeCtx = useActiveContext();

  const [statusFilter, setStatusFilter] = createSignal<StatusFilter>("all");

  // ── Deep-link status filter ↔ URL. The URL `?status=` is the source of truth:
  // this effect reflects it (and back/forward / pasted links) into the signal,
  // and `applyStatusFilter` writes user clicks back so the URL stays copyable and
  // reloading reconstructs exactly the same view (idempotent). Default ("all") is
  // omitted to keep URLs clean.
  createEffect(() => {
    const s = search().status;
    if (s === "all" || s === "draft" || s === "in_progress" || s === "completed") {
      setStatusFilter(s);
    } else if (s === undefined) {
      setStatusFilter("all");
    }
  });

  const applyStatusFilter = (value: StatusFilter) => {
    setStatusFilter(value);
    void navigate({
      to: "/inspections",
      search: (prev) => ({ ...prev, status: value === "all" ? undefined : value }),
      replace: true,
    });
  };

  /** Optional site/template scope coming from a site row or template detail. */
  const siteScopeId = () => search().siteId;
  const templateScopeId = () => search().templateId;

  // ── "New inspection" dialog ↔ URL. `?new=inspection` is the source of truth so
  // the dialog has a copyable link that reopens it (idempotent). open/close write
  // the URL with `replace:true` to keep the dialog out of history.
  const dialogOpen = () => search().new === "inspection";
  const openNew = () => {
    void navigate({
      to: "/inspections",
      search: (prev) => ({ ...prev, new: "inspection" }),
      replace: true,
    });
  };
  const closeNew = () => {
    void navigate({
      to: "/inspections",
      search: (prev) => ({ ...prev, new: undefined }),
      replace: true,
    });
  };

  const [templateId, setTemplateId] = createSignal("");
  const [siteId, setSiteId] = createSignal("");
  const [inspectorId, setInspectorId] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);

  const inspections = createPowerSyncQuery<InspectionWithMeta>({
    queryKey: () => queryKeys.inspections.list(userId(), undefined, activeCtx.activeOrgId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getInspectionsWithMetaQuery(dbInstance, { scope: activeCtx.scope() })
        : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const filtered = createMemo(() => {
    const list = inspections.data ?? [];
    const filter = statusFilter();
    const scopeSite = siteScopeId();
    const scopeTemplate = templateScopeId();
    return list.filter((i) => {
      if (filter !== "all" && i.status !== filter) {
        return false;
      }
      if (scopeSite && i.site_id !== scopeSite) {
        return false;
      }
      if (scopeTemplate && i.template_id !== scopeTemplate) {
        return false;
      }
      return true;
    });
  });

  const scopeLabel = createMemo(() => {
    const scopeSite = siteScopeId();
    if (scopeSite) {
      const match = (inspections.data ?? []).find((i) => i.site_id === scopeSite);
      return match?.site_name ?? t(msg`Selected site`);
    }
    const scopeTemplate = templateScopeId();
    if (scopeTemplate) {
      const match = (inspections.data ?? []).find((i) => i.template_id === scopeTemplate);
      return match?.template_name ?? t(msg`Selected template`);
    }
    return null;
  });

  const clearScope = () => {
    void navigate({
      to: "/inspections",
      search: (prev) => ({ ...prev, siteId: undefined, templateId: undefined }),
    });
  };

  const templates = createPowerSyncQuery<TemplateRecord>({
    queryKey: () => queryKeys.templates.list(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getTemplatesQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const sites = createPowerSyncQuery<SiteRecord>({
    queryKey: () => queryKeys.sites.list(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getSitesQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const selectedTemplate = () => templates.data?.find((tpl) => tpl.id === templateId());
  const selectedOrgId = () => selectedTemplate()?.organization_id ?? null;

  const members = createPowerSyncQuery<OrgMemberRow>({
    queryKey: () => queryKeys.organizations.localMembers(selectedOrgId() ?? undefined),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const orgId = selectedOrgId();
      return dbInstance && orgId ? getOrgMembersLocalQuery(dbInstance, orgId) : null;
    },
    enabled: () => Boolean(drizzle() && selectedOrgId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  // Seed the form ONLY on dialog open transition (CC-16 /
  // CC-26). The previous effect read `templates.data` reactively, so
  // EVERY query re-emit (new identity via `keepPreviousData` or a sync
  // tick) while dialog was open RESET `templateId`/`siteId`/
  // `inspectorId` → cleared user selection right before "Create"
  // (Select backed by query that "loses click"). Now reset only
  // once on open; initial template is seeded by guarded effect
  // below (which does not re-trigger once user selects).
  let dialogWasOpen = false;
  createEffect(() => {
    const open = dialogOpen();
    if (open && !dialogWasOpen) {
      setTemplateId("");
      setSiteId("");
      setInspectorId("");
    }
    dialogWasOpen = open;
  });

  // Lazy seed of first template if dialog opened before query
  // loaded. Guarded on `templateId() === ""`: a user selection
  // (non-empty value) is NEVER overwritten, so subsequent query re-emit
  // cannot stomp selection.
  createEffect(() => {
    if (dialogOpen() && templateId() === "" && (templates.data?.length ?? 0) > 0) {
      setTemplateId(templates.data?.[0]?.id ?? "");
    }
  });

  const handleCreate = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const uid = userId();
    if (!dbInstance || !uid || !templateId()) {
      return;
    }
    setIsSubmitting(true);
    try {
      const id = await createInspectionMutation(dbInstance, {
        templateId: templateId(),
        siteId: siteId() || null,
        userId: uid,
        organizationId: selectedOrgId(),
        inspectorId: inspectorId() || uid,
      });
      void navigate({ to: "/inspections/$inspectionId", params: { inspectionId: id } });
    } catch (err) {
      console.error("Error creating inspection:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusBadge = (status: string | null) => (
    <Show
      when={status === "completed"}
      fallback={
        <Show
          when={status === "in_progress"}
          fallback={
            <Badge variant="secondary" class="gap-1">
              <CircleDashed class="h-3 w-3" />
              {t(STATUS_LABELS.draft)}
            </Badge>
          }
        >
          <Badge variant="secondary" class="gap-1 text-warning">
            <Play class="h-3 w-3" />
            {t(STATUS_LABELS.in_progress)}
          </Badge>
        </Show>
      }
    >
      <Badge variant="secondary" class="gap-1 text-success">
        <CheckCircle2 class="h-3 w-3" />
        {t(STATUS_LABELS.completed)}
      </Badge>
    </Show>
  );

  const FILTERS: { key: StatusFilter; label: MessageDescriptor }[] = [
    { key: "all", label: msg`All` },
    { key: "draft", label: STATUS_LABELS.draft },
    { key: "in_progress", label: STATUS_LABELS.in_progress },
    { key: "completed", label: STATUS_LABELS.completed },
  ];

  return (
    <PageContainer maxWidth="4xl" class="overflow-y-auto">
      <div class="flex items-center justify-between gap-4 mb-4">
        <PageHeader
          title={t(msg`Inspections`)}
          description={t(msg`Run checklists against your sites`)}
          icon={ClipboardCheck}
        />
        <Button
          onClick={() => {
            openNew();
          }}
          class="gap-2 shrink-0"
          data-testid="new-inspection"
        >
          <Plus class="h-4 w-4" />
          {t(msg`New inspection`)}
        </Button>
      </div>

      {/* Filtro por estado: pestañas animadas (Kobalte Tabs + transición de panel
          GPU). Cambiar de filtro desliza el panel de la lista (kit #8). */}
      <Tabs
        value={statusFilter()}
        onChange={(value) => {
          if (isStatusFilter(value)) {
            applyStatusFilter(value);
          }
        }}
      >
        <TabsList class="mb-4 w-fit gap-1 rounded-xl border-0 bg-muted/40 p-1">
          <For each={FILTERS}>
            {(f) => (
              <TabsTrigger
                value={f.key}
                class={cn(
                  "rounded-lg px-3 py-1.5 text-xs font-bold",
                  "data-[selected]:bg-background data-[selected]:text-foreground data-[selected]:shadow-sm",
                )}
              >
                {t(f.label)}
              </TabsTrigger>
            )}
          </For>
        </TabsList>

        <Show when={scopeLabel()}>
          {(label) => (
            <div class="mb-4 flex items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2.5">
              <span class="text-sm font-medium text-primary">
                {siteScopeId() ? t(msg`Inspections at`) : t(msg`Inspections using`)}{" "}
                <span class="text-muted-foreground">{label()}</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                class="gap-1.5 shrink-0"
                onClick={clearScope}
                aria-label={t(msg`Clear filter`)}
              >
                <X class="h-3.5 w-3.5" />
                {t(msg`Clear`)}
              </Button>
            </div>
          )}
        </Show>

        <TabsPanels value={statusFilter()} order={FILTERS.map((f) => f.key)}>
          <TabsPanel value={statusFilter()}>
            <Show
              when={!inspections.isLoading}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-20 w-full rounded-xl" />
                  <Skeleton class="h-20 w-full rounded-xl" />
                </div>
              }
            >
              <Show
                when={filtered().length > 0}
                fallback={
                  <EmptyState
                    icon={ClipboardCheck}
                    illustration={ClipboardChecklistIllustration}
                    title={t(msg`No inspections`)}
                    description={t(
                      msg`Create one from a template and run it on site, even offline.`,
                    )}
                    onIconActivate={() => {
                      openNew();
                    }}
                    iconActionLabel={t(msg`New inspection`)}
                    action={
                      <Button
                        class="gap-2"
                        onClick={() => {
                          openNew();
                        }}
                      >
                        <Plus class="h-4 w-4" />
                        {t(msg`New inspection`)}
                      </Button>
                    }
                  />
                }
              >
                <div class="grid gap-3" data-testid="inspections-list">
                  <For each={filtered()}>
                    {(insp, index) => (
                      <button
                        type="button"
                        class="text-left group animate-slide-in-up"
                        style={{
                          "animation-delay": `${Math.min(index(), 8) * 40}ms`,
                          "animation-fill-mode": "both",
                        }}
                        data-testid="inspection-card"
                        onClick={() =>
                          void navigate({
                            to: "/inspections/$inspectionId",
                            params: { inspectionId: insp.id },
                          })
                        }
                      >
                        <Card class="transition-all group-hover:border-primary/50 group-hover:shadow-md">
                          <CardContent class="flex items-center gap-4 p-4">
                            <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                              <ClipboardCheck class="h-5 w-5" />
                            </div>
                            <div class="min-w-0 flex-1">
                              <div class="flex items-center gap-2">
                                <h3 class="font-semibold truncate">
                                  {insp.template_name ?? t(msg`Inspection`)}
                                </h3>
                                {statusBadge(insp.status)}
                              </div>
                              <div class="mt-0.5 flex items-center gap-3 text-sm text-muted-foreground">
                                <Show when={insp.site_name}>
                                  <span class="flex items-center gap-1 truncate">
                                    <MapPin class="h-3.5 w-3.5 shrink-0" />
                                    {insp.site_name}
                                  </span>
                                </Show>
                                <Show when={insp.inspector_name}>
                                  <span class="flex items-center gap-1 truncate">
                                    <User class="h-3.5 w-3.5 shrink-0" />
                                    {insp.inspector_name}
                                  </span>
                                </Show>
                              </div>
                            </div>
                            <ChevronRight class="h-5 w-5 text-muted-foreground shrink-0 transition-transform group-hover:translate-x-0.5" />
                          </CardContent>
                        </Card>
                      </button>
                    )}
                  </For>
                </div>
              </Show>
            </Show>
          </TabsPanel>
        </TabsPanels>
      </Tabs>

      {/* Nueva inspección */}
      <Dialog
        open={dialogOpen()}
        onOpenChange={(open) => {
          if (!open) {
            closeNew();
          }
        }}
        contentClass="max-w-md p-0"
      >
        <DialogHeader size="md">
          <DialogTitle class="text-xl font-bold">{t(msg`New inspection`)}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => void handleCreate(e)} class="space-y-4 p-6">
          <div class="space-y-2">
            <Label for="inspection-template">{t(msg`Template`)}</Label>
            <Select
              id="inspection-template"
              value={templateId()}
              onChange={setTemplateId}
              placeholder={
                (templates.data?.length ?? 0) === 0
                  ? t(msg`No templates — create one first`)
                  : undefined
              }
              options={(templates.data ?? []).map((tpl) => ({
                value: tpl.id,
                label: tpl.name ?? "",
              }))}
              emptyAction={{
                label: t(msg`Create a template`),
                href: "/templates",
                description: t(msg`No templates yet`),
              }}
            />
          </div>

          <div class="space-y-2">
            <Label for="inspection-site">{t(msg`Site`)}</Label>
            <Select
              id="inspection-site"
              value={siteId()}
              onChange={setSiteId}
              options={[
                { value: "", label: t(msg`No site`) },
                ...(sites.data ?? []).map((s) => ({ value: s.id, label: s.name ?? "" })),
              ]}
            />
          </div>

          <Show when={selectedOrgId()}>
            <div class="space-y-2">
              <Label for="inspection-inspector">{t(msg`Inspector`)}</Label>
              <Select
                id="inspection-inspector"
                value={inspectorId()}
                onChange={setInspectorId}
                options={[
                  { value: "", label: t(msg`Me`) },
                  ...(members.data ?? []).map((m) => ({
                    value: m.user_id ?? "",
                    label: m.name ?? m.email ?? m.user_id ?? "",
                  })),
                ]}
              />
            </div>
          </Show>

          <div class="flex items-center justify-end gap-3 pt-4 border-t border-border/10">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                closeNew();
              }}
            >
              {t(msg`Cancel`)}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting() || !templateId() || !drizzle()}
              class="disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="create-inspection"
            >
              {t(msg`Create inspection`)}
            </Button>
          </div>
        </form>
      </Dialog>
    </PageContainer>
  );
}
